import { useEffect, useMemo, useState, type ComponentType } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { Trophy, Building,
  FileText, Package, Plug, Receipt, Truck, TrendingUp, Users2,
  Activity, Building2, CalendarClock, CalendarDays, Clock, CreditCard, Home, ListChecks, LogOut, MapPinned, Megaphone, Menu, MessageCircle, Monitor, Moon,
  PhoneCall, ShieldCheck, Smartphone, Sun, Target, UserCircle, Users, X, Sparkles, Globe, Bot, Wallet, Settings, PhoneForwarded, Hand, Zap, ListTodo, CalendarOff, MessagesSquare, BookOpen, LifeBuoy, PanelsTopLeft, BarChart3,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Permission } from '#shared';
import { useAuth } from '../lib/auth';
import { getStoredTheme, setTheme, type ThemeChoice } from '../lib/theme';
import { cx } from './ui';
import { NotificationBell } from './NotificationBell';
import { AskAi } from './AskAi';
import { AnnouncementBanner } from './Announcements';
import { embedded } from '../lib/embedded';
import { MyStatus } from './TeamStatus';
import { RecordPanels } from './RecordPanels';

export interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** Shown only to people with this permission (or any one of these). */
  permission?: Permission | Permission[];
  keywords?: string;
  badge?: 'followups' | 'whatsapp' | 'webchat' | 'chat';
  /** Shown only when the company uses this feature (or to people who can set it up). */
  onlyIfUsed?: 'inventory';
}
export interface NavGroup {
  label?: string;
  items: NavItem[];
}

export const TENANT_NAV: NavGroup[] = [
  {
    items: [
      { to: '/', label: 'Home', icon: Home, keywords: 'dashboard start' },
      { to: '/leaderboard', label: 'Leaderboard', icon: Trophy, permission: 'reports.view_all', keywords: 'ranking top performers points competition best salesperson' },
      { to: '/reports', label: 'Reports', icon: BarChart3, keywords: 'report builder chart excel scheduled email compliance habits analytics' },
    ],
  },
  {
    label: 'Sales',
    items: [
      { to: '/leads', label: 'Leads', icon: Target, keywords: 'customers enquiries contacts kanban board pipeline import export' },
      { to: '/followups', label: 'Follow-ups', icon: CalendarClock, keywords: 'reminders tasks due overdue callback', badge: 'followups' },
      { to: '/calendar', label: 'Calendar', icon: CalendarDays, keywords: 'schedule' },
      { to: '/call-list', label: 'Call list', icon: PhoneForwarded, keywords: 'dialer auto dialer power dialer queue today calls next' },
      { to: '/lead-pool', label: 'Lead pool', icon: Hand, permission: 'leads.pool', keywords: 'unassigned claim take pick leads' },
      { to: '/calls', label: 'Calls', icon: PhoneCall, keywords: 'call log recordings talk time report' },
      { to: '/calls/insights', label: 'Call insights', icon: Sparkles, keywords: 'ai call review rating sentiment audit coaching transcript' },
      { to: '/webchat', label: 'Website chats', icon: Globe, permission: 'webchat.chat', keywords: 'web chat widget visitors bot', badge: 'webchat' },
    ],
  },
  {
    label: 'Billing',
    items: [
      { to: '/sales/documents', label: 'Quotes & invoices', icon: FileText, permission: ['documents.manage', 'documents.view_all'], keywords: 'quotation invoice proforma bill gst estimate pdf' },
      { to: '/sales/customers', label: 'Customers', icon: Users2, permission: ['documents.manage', 'documents.view_all'], keywords: 'clients parties ledger outstanding' },
      { to: '/sales/products', label: 'Products', icon: Package, keywords: 'services items price list hsn' },
      { to: '/sales/reports', label: 'Sales & targets', icon: TrendingUp, permission: ['documents.manage', 'documents.view_all', 'reports.view_all', 'targets.manage'], keywords: 'revenue target gstr gst report collection outstanding' },
      { to: '/sales/closure', label: 'After the sale', icon: Truck, keywords: 'delivery closure project won' },
      { to: '/inventory', label: 'Inventory', icon: Building, onlyIfUsed: 'inventory', keywords: 'flats plots villas units availability real estate property hold block sold tower project' },
    ],
  },
  {
    label: 'WhatsApp',
    items: [
      { to: '/whatsapp', label: 'Chats', icon: MessageCircle, permission: 'whatsapp.chat', keywords: 'whatsapp inbox messages chat', badge: 'whatsapp' },
      { to: '/whatsapp/campaigns', label: 'Campaigns', icon: Megaphone, permission: 'whatsapp.campaigns', keywords: 'bulk broadcast template marketing' },
    ],
  },
  {
    label: 'Work',
    items: [
      { to: '/chat', label: 'Team chat', icon: MessagesSquare, keywords: 'message colleague group internal chat', badge: 'chat' },
      { to: '/tasks', label: 'Tasks', icon: ListTodo, keywords: 'to do todo task plan my day what i did today daily report' },
      { to: '/hr/leaves', label: 'Leaves', icon: CalendarOff, keywords: 'holiday leave off sick casual apply approve' },
      { to: '/hr/expenses', label: 'Expenses', icon: Receipt, keywords: 'claim reimbursement travel km bill petrol food' },
      { to: '/hr/salary', label: 'Salary', icon: Wallet, keywords: 'payslip payroll pay bank account salary slip' },
      { to: '/kb', label: 'Knowledge base', icon: BookOpen, keywords: 'scripts faq offers brochure price list videos notes help' },
    ],
  },
  {
    label: 'Field team',
    items: [
      { to: '/attendance', label: 'Attendance', icon: Clock, keywords: 'check in check out present absent late' },
      { to: '/team-map', label: 'Team map', icon: MapPinned, permission: 'tracking.view_team', keywords: 'location gps tracking live route km travel' },
    ],
  },
  {
    label: 'Team',
    items: [
      { to: '/team', label: 'Team members', icon: Users, permission: 'users.view', keywords: 'users staff people employees' },
      { to: '/team/roles', label: 'Roles & access', icon: ShieldCheck, permission: 'users.view', keywords: 'permissions rights' },
      { to: '/team/announcements', label: 'Announcements', icon: Megaphone, permission: 'users.manage', keywords: 'notice news broadcast team message' },
      { to: '/team/menu', label: 'Menu', icon: PanelsTopLeft, permission: 'roles.manage', keywords: 'hide menu items order arrange sort simplify sidebar' },
    ],
  },
  {
    label: 'Settings',
    items: [
      { to: '/settings/company', label: 'Company profile', icon: Building2, permission: 'settings.company', keywords: 'logo gst address brand colour' },
      { to: '/settings/integrations', label: 'Lead capture', icon: Plug, permission: 'settings.manage', keywords: 'lead sources landing page website api webhook facebook lead ads indiamart tradeindia justdial form qr zapier integration' },
      { to: '/settings/leads', label: 'Lead setup', icon: ListChecks, permission: 'settings.manage', keywords: 'status stages sources tags campaigns fields auto assign round robin routing' },
      { to: '/settings/field', label: 'Calls & field', icon: Smartphone, permission: 'settings.manage', keywords: 'tracking gps office attendance missed call allowance' },
      { to: '/settings/billing', label: 'Invoice settings', icon: Receipt, permission: 'settings.company', keywords: 'bank upi prefix terms gst state' },
      { to: '/settings/whatsapp', label: 'WhatsApp setup', icon: MessageCircle, permission: 'settings.manage', keywords: 'meta cloud api templates webhook quick replies connect' },
      { to: '/settings/automation', label: 'Automation', icon: Zap, permission: 'settings.manage', keywords: 'rules workflow trigger auto sequence drip nurture sla tat response time lead pool' },
      { to: '/settings/ai', label: 'AI assistant', icon: Bot, permission: 'settings.manage', keywords: 'ai bot chatbot auto reply knowledge scoring hot leads website chat' },
      { to: '/settings/plan', label: 'Plan & credits', icon: Wallet, permission: 'billing.manage', keywords: 'subscription renew pay payment cashfree credits recharge top up wallet' },
      { to: '/support', label: 'Help & support', icon: LifeBuoy, keywords: 'ticket help problem contact support feature request' },
      { to: '/settings/activity', label: 'Activity log', icon: Activity, permission: 'audit.view', keywords: 'audit history' },
    ],
  },
];

const PLATFORM_NAV: NavGroup[] = [
  {
    items: [
      { to: '/platform', label: 'Overview', icon: Home },
      { to: '/platform/companies', label: 'Companies', icon: Building2, keywords: 'tenants customers clients' },
      { to: '/platform/plans', label: 'Plans & pricing', icon: CreditCard, keywords: 'subscription price' },
      { to: '/platform/billing', label: 'Revenue & AI usage', icon: TrendingUp, keywords: 'payments income credits cost' },
      { to: '/platform/tickets', label: 'Support tickets', icon: LifeBuoy, keywords: 'help questions problems' },
      { to: '/platform/releases', label: 'Android app', icon: Smartphone, keywords: 'apk mobile update version release' },
      { to: '/platform/announcements', label: 'Announcements', icon: Megaphone, keywords: 'notice news broadcast' },
      { to: '/platform/settings', label: 'AI & payments setup', icon: Settings, keywords: 'claude api key cashfree deepgram rates' },
    ],
  },
];

/** The company's own menu order: groups by label, items by address. Anything not listed keeps its place after the listed ones. */
export function applyMenuOrder(nav: NavGroup[], groupOrder: string[], itemOrder: string[]): NavGroup[] {
  const rank = (list: string[], key: string, fallback: number) => { const i = list.indexOf(key); return i === -1 ? list.length + fallback : i; };
  return nav
    .map((g, gi) => ({ g: { ...g, items: g.items.map((it, ii) => ({ it, r: rank(itemOrder, it.to, ii) })).sort((a, b) => a.r - b.r).map((x) => x.it) }, r: rank(groupOrder, g.label ?? '', gi) }))
    .sort((a, b) => (a.g.label ? a.r : -1) - (b.g.label ? b.r : -1))
    .map((x) => x.g);
}

export function AppShell({ area }: { area: 'tenant' | 'platform' }) {
  const { me, can } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  useEffect(() => setMobileOpen(false), [location.pathname]);

  const menu = useQuery({
    queryKey: ['settings', 'menu'],
    queryFn: () => api.get<{ hidden: Record<string, string[]>; order: string[]; groupOrder: string[] }>('/settings/menu'),
    enabled: area === 'tenant' && !!me?.tenant && me.tenant.status !== 'suspended',
    staleTime: 5 * 60_000,
  });
  const inventory = useQuery({
    queryKey: ['inventory', 'count'],
    queryFn: () => api.get<{ n: number }>('/inventory/count'),
    enabled: area === 'tenant' && !!me?.tenant && me.tenant.status !== 'suspended' && !can('inventory.manage'),
    staleTime: 10 * 60_000,
  });
  const used = { inventory: can('inventory.manage') || (inventory.data?.n ?? 0) > 0 };
  // Items an admin hid for this person's role (admins always see everything).
  const hiddenKey = me?.role && me.role.key !== 'admin' ? (menu.data?.hidden[me.role.id] ?? []).join('|') : '';
  const orderKey = area === 'tenant' ? `${(menu.data?.groupOrder ?? []).join('|')}#${(menu.data?.order ?? []).join('|')}` : '';
  const groups = useMemo(() => {
    const hidden = hiddenKey ? hiddenKey.split('|') : [];
    const [groupOrder, itemOrder] = orderKey.split('#').map((x) => (x ? x.split('|') : []));
    return applyMenuOrder(area === 'platform' ? PLATFORM_NAV : TENANT_NAV, groupOrder ?? [], itemOrder ?? [])
      .map((g) => ({ ...g, items: g.items.filter((i) => (!i.permission || [i.permission].flat().some(can)) && !hidden.includes(i.to) && (!i.onlyIfUsed || used[i.onlyIfUsed])) }))
      .filter((g) => g.items.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [area, can, hiddenKey, orderKey, used.inventory]);
  const title = area === 'platform' ? 'AI CRM · Owner' : me?.tenant?.name ?? 'AI CRM';

  const sidebar = <Sidebar groups={groups} title={title} area={area} />;

  // Inside the Android app: just the page. The app provides the top bar, back button and menu.
  if (embedded) {
    return (
      <main className="h-full overflow-y-auto">
        <div className="mx-auto max-w-6xl px-4 py-4">
          <Outlet />
        </div>
        {area === 'tenant' && <RecordPanels />}
      </main>
    );
  }

  return (
    <div className="flex h-full">
      <aside className="hidden w-64 shrink-0 border-r border-slate-200 bg-white lg:block dark:border-slate-800 dark:bg-slate-900">
        {sidebar}
      </aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMobileOpen(false)} aria-hidden />
          <aside className="absolute inset-y-0 left-0 w-72 bg-white shadow-xl dark:bg-slate-900">
            <button onClick={() => setMobileOpen(false)} className="absolute top-3 right-3 rounded-md p-1.5 text-slate-500" aria-label="Close menu">
              <X className="size-5" />
            </button>
            {sidebar}
          </aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center gap-3 border-b border-slate-200 bg-white px-4 lg:hidden dark:border-slate-800 dark:bg-slate-900">
          <button onClick={() => setMobileOpen(true)} className="rounded-md p-1.5 text-slate-600" aria-label="Open menu">
            <Menu className="size-6" />
          </button>
          <span className="flex-1 truncate font-semibold">{title}</span>
          {area === 'tenant' && <NotificationBell align="right" />}
        </header>
        <TrialBanner />
        {area === 'tenant' && <AnnouncementBanner />}
        <main className="flex-1 overflow-y-auto">
          <div className={cx('mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8 lg:py-8', area === 'tenant' && 'pb-24 lg:pb-24')}>
            <Outlet />
          </div>
        </main>
        {area === 'tenant' && <AskAi />}
        {area === 'tenant' && <RecordPanels />}
      </div>
    </div>
  );
}

function Sidebar({ groups, title, area }: { groups: NavGroup[]; title: string; area: 'tenant' | 'platform' }) {
  const { me, logout } = useAuth();
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  // A parent item (e.g. Calls) shouldn't light up while a child item (Call insights) is open.
  const allTos = groups.flatMap((g) => g.items.map((i) => i.to));
  const visible = q
    ? groups
        .map((g) => ({ ...g, items: g.items.filter((i) => `${i.label} ${i.keywords ?? ''}`.toLowerCase().includes(q)) }))
        .filter((g) => g.items.length)
    : groups;

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-14 items-center gap-2.5 px-4">
        {me?.tenant?.logoUrl ? (
          <img src={me.tenant.logoUrl} alt="" className="size-8 shrink-0 rounded-lg object-contain" />
        ) : (
          <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-brand-600 text-xs font-bold text-white">AI</div>
        )}
        <span className="flex-1 truncate font-semibold" title={title}>{title}</span>
        {area === 'tenant' && <NotificationBell />}
      </div>
      <div className="px-3 pb-2">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Find a page…"
          aria-label="Find a page"
          className="h-9 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 text-sm placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-500/30 focus:outline-none dark:border-slate-700 dark:bg-slate-800"
        />
      </div>
      <nav className="flex-1 space-y-4 overflow-y-auto px-3 py-2" aria-label="Main">
        {visible.map((g, gi) => (
          <div key={g.label ?? gi}>
            {g.label && <p className="mb-1 px-2 text-xs font-semibold tracking-wide text-slate-500 uppercase">{g.label}</p>}
            <ul className="space-y-0.5">
              {g.items.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === '/' || item.to === '/platform' || allTos.some((t) => t.startsWith(`${item.to}/`))}
                    className={({ isActive }) =>
                      cx(
                        'flex items-center gap-3 rounded-lg px-2.5 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-white'
                          : 'text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
                      )
                    }
                  >
                    <item.icon className="size-4.5 shrink-0" />
                    <span className="flex-1">{item.label}</span>
                    {item.badge === 'followups' && <FollowupBadgeCount />}
                    {item.badge === 'whatsapp' && <WhatsAppUnread />}
                    {item.badge === 'webchat' && <WebchatWaiting />}
                    {item.badge === 'chat' && <ChatUnread />}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        ))}
        {!visible.length && <p className="px-2 text-sm text-slate-500">No page matches “{query}”.</p>}
      </nav>
      <div className="border-t border-slate-200 p-3 dark:border-slate-800">
        <NavLink
          to={area === 'platform' ? '/platform/profile' : '/profile'}
          className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-slate-100 dark:hover:bg-slate-800"
        >
          <UserCircle className="size-8 shrink-0 text-slate-400" />
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium">{me?.name}</span>
            <span className="block truncate text-xs text-slate-500">{me?.role?.name ?? 'Owner'}</span>
          </span>
        </NavLink>
        {area === 'tenant' && <div className="mt-1"><MyStatus /></div>}
        <div className="mt-2 flex items-center justify-between gap-2 px-1">
          <ThemeSwitch />
          <button
            onClick={logout}
            className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            <LogOut className="size-4" /> Sign out
          </button>
        </div>
      </div>
    </div>
  );
}

function ThemeSwitch() {
  const [choice, setChoice] = useState<ThemeChoice>(getStoredTheme);
  const options: { value: ThemeChoice; icon: typeof Sun; label: string }[] = [
    { value: 'light', icon: Sun, label: 'Light' },
    { value: 'dark', icon: Moon, label: 'Dark' },
    { value: 'system', icon: Monitor, label: 'Same as device' },
  ];
  return (
    <div className="flex rounded-lg bg-slate-100 p-0.5 dark:bg-slate-800" role="radiogroup" aria-label="Theme">
      {options.map((o) => (
        <button
          key={o.value}
          role="radio"
          aria-checked={choice === o.value}
          title={o.label}
          onClick={() => { setChoice(o.value); setTheme(o.value); }}
          className={cx('rounded-md p-1.5', choice === o.value ? 'bg-white shadow-sm dark:bg-slate-700' : 'text-slate-500')}
        >
          <o.icon className="size-4" />
          <span className="sr-only">{o.label}</span>
        </button>
      ))}
    </div>
  );
}

function TrialBanner() {
  const { me, can } = useAuth();
  const t = me?.tenant;
  if (!t) return null;
  let text: React.ReactNode = null;
  let urgent = false;
  if (t.status === 'trial' && t.trialEndsAt) {
    const days = Math.max(0, Math.ceil((new Date(t.trialEndsAt).getTime() - Date.now()) / 86_400_000));
    text = <>You’re on a free trial — <strong>{days} day{days === 1 ? '' : 's'} left</strong>.</>;
    urgent = days <= 3;
  } else if (t.status === 'active' && t.paidUntil) {
    const days = Math.ceil((new Date(t.paidUntil).getTime() - Date.now()) / 86_400_000);
    if (days > 7) return null;
    text = days >= 0
      ? <>Your plan ends in <strong>{days} day{days === 1 ? '' : 's'}</strong>.</>
      : <>Your plan has ended. <strong>Renew now</strong> to keep using AI CRM.</>;
    urgent = days < 0;
  }
  if (!text) return null;
  return (
    <div className={cx('border-b px-4 py-2 text-center text-sm', urgent
      ? 'border-red-200 bg-red-50 text-red-900 dark:border-red-900 dark:bg-red-950 dark:text-red-200'
      : 'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200')}>
      {text}{' '}
      {can('billing.manage') && <NavLink to="/settings/plan" className="font-semibold underline">{t.status === 'trial' ? 'Choose a plan' : 'Renew'}</NavLink>}
    </div>
  );
}

/** Red badge with "late + due today" follow-ups for the signed-in person. */
function FollowupBadgeCount() {
  const counts = useQuery({
    queryKey: ['followup-counts'],
    queryFn: () => api.get<{ overdue: number; dueToday: number }>('/followups/counts'),
    refetchInterval: 60_000,
  });
  const n = (counts.data?.overdue ?? 0) + (counts.data?.dueToday ?? 0);
  if (!n) return null;
  return (
    <span className={cx('rounded-full px-1.5 text-xs font-semibold', counts.data!.overdue ? 'bg-red-600 text-white' : 'bg-amber-100 text-amber-800')}
      title={`${counts.data!.overdue} late, ${counts.data!.dueToday} due today`}>
      {n}
    </span>
  );
}

/** Green badge with unread WhatsApp conversations. */
function WhatsAppUnread() {
  const q = useQuery({
    queryKey: ['whatsapp', 'threads', 'unread', ''],
    queryFn: () => api.get<{ unreadCount: number }[]>('/whatsapp/threads?view=unread'),
    refetchInterval: 30_000,
    retry: false,
  });
  const n = q.data?.length ?? 0;
  if (!n) return null;
  return <span className="rounded-full bg-emerald-600 px-1.5 text-xs font-semibold text-white">{n}</span>;
}

/** Website chats waiting for a person. */
function WebchatWaiting() {
  const q = useQuery({
    queryKey: ['webchat', 'sessions', 'needs_human'],
    queryFn: () => api.get<unknown[]>('/webchat/sessions?filter=needs_human'),
    refetchInterval: 30_000,
    retry: false,
  });
  const n = q.data?.length ?? 0;
  if (!n) return null;
  return <span className="rounded-full bg-sky-600 px-1.5 text-xs font-semibold text-white">{n}</span>;
}

/** Unread team-chat messages. */
function ChatUnread() {
  const q = useQuery({ queryKey: ['chat', 'unread'], queryFn: () => api.get<{ n: number }>('/chat/unread'), refetchInterval: 20_000, retry: false });
  if (!q.data?.n) return null;
  return <span className="rounded-full bg-brand-600 px-1.5 text-xs font-semibold text-white">{q.data.n}</span>;
}
